<template>
  <modal v-on="$listeners">
    <template v-slot:header>{{ pageName }}</template>

    <!-- default slot -->
    <div class="flex flex-column min-modal-height">
      <div class="flex-auto flex flex-column justify-center">
        <div v-if="hasRecording" class="p2 center mb3">
          <div v-if="converting" class="relative mx2 left-align">
            <div class="absolute left-0 right-0 bottom-0">
              <div class="h6 silver">Creating MP3...</div>
              <div :style="{ width: `${progress}%` }" class="progress-bar bg-gray rounded my1"></div>
            </div>
          </div>
          <audio controls controlsList="nodownload" class="col-12">
            <source :src="fileInfo.href" :type="fileType" :key="fileInfo.href">
          </audio>
          <div v-if="prepareUpload" class="mt3">
            <field-note-form :draft="draft" :file="file" @close="$emit('close')" type="sound"/>
          </div>
        </div>
        <div v-show="!hasRecording">
          <div v-if="audioUnavailable" class="p1 center">
            <span>Could not access<br>microphone</span>
          </div>
          <div v-else-if="!audioReady" class="p1 center">
            <a @click="initAudio" class="pointer">Click microphone<br>to enable audio</a>
            <div class="h6 gray mt3 px3">If you're unable to access the microphone, try using the browser instead of the app, or upload a recording from your desktop.</div>
          </div>
          <div v-else class="p1">
            <div>
              <canvas ref="can" width="600" height="300" style="max-width:100%"></canvas>
            </div>
          </div>
        </div>
      </div>
      <div v-if="!prepareUpload" class="controls p1 pb3 center relative">
        <div v-if="hasRecording" class="flex justify-center items-center h6">
          <div class="mx1">
            <button @click="startOver" class="btn btn-outline btn-rounded">START OVER</button>
          </div>
          <div class="mx1">
            <button @click="addInfo" class="btn btn-outline white bg-black btn-rounded">CONTINUE</button>
          </div>
        </div>
        <div v-if="!audioUnavailable && !hasRecording">
          <a @click="initAudio" v-if="!audioReady">
            <microphone/>
          </a>
          <span v-else @click="toggleRecording" :class="{ pointer: audioReady, fade: !audioReady }">
            <record-icon :active="activeRecording"/>
          </span>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
  import Recorder from 'recorderjs'
  import Microphone from '@/components/icons/Microphone'
  import FieldNoteForm from '@/components/FieldNoteForm'
  import { createObjectURL } from '@/util'

  // mp3 worker
  import MP3Worker from '@/mp3.worker.js'

  // stereo or mono?
  const numChannels = 1

  export default {
    name: 'NewAudio',
    metaInfo: {
      title: 'New Recording - Field Notes'
    },
    props: {
      draft: Object
    },
    data () {
      return {
        file: null,
        addNew: false,
        audioReady: false,
        audioUnavailable: false,
        streamReference: null,
        audioContext: null,
        activeRecording: false,
        audioInput: null,
        realAudioInput: null,
        inputPoint: null,
        audioRecorder: null,
        rafID: null,
        analyserNode: null,
        analyserContext: null,
        canvasWidth: null,
        canvasHeight: null,
        recIndex: 0,
        // filesVisible: false,
        prepareUpload: false,

        recording: null,
        mp3: null,
        buffers: null,
        converting: false,
        progress: 0
      }
    },
    created () {
      if (this.draft) {
        this.prepareUpload = true
      }
    },
    computed: {
      pageName () {
        return this.draft ? 'Edit Note' : 'New Recording'
      },
      fileInfo () {
        return this.mp3 || this.recording || {
          href: this.draft && this.draft.previewUrl
        }
      },
      hasRecording () {
        return !!this.fileInfo.href || this.draft
      },
      fileType () {
        if (!this.hasRecording) return
        return this.recording ? 'audio/wav' : 'audio/mp3'
      }
    },
    methods: {
      addInfo () {
        this.prepareUpload = true
        this.makeMP3()
      },
      saveAudio () {
        this.audioRecorder.exportWAV(this.doneEncoding)
      },
      gotBuffers (buffers) {
        this.buffers = buffers
        this.audioRecorder.exportWAV(this.doneEncoding)
      },
      doneEncoding (blob) {
        const href = createObjectURL(blob)
        const date = new Date()
        const download = `audio-${date.getTime()}.wav`

        this.recording = { href, download }
        blob.name = download
        this.file = blob
      },
      toggleRecording () {
        if (!this.audioReady) return
        if (this.activeRecording) {
          this.audioRecorder.stop()
          this.activeRecording = false
          this.audioRecorder.getBuffer(this.gotBuffers)
        } else {
          if (!this.audioRecorder) return
          this.activeRecording = true
          this.audioRecorder.clear()
          this.audioRecorder.record()
        }
      },
      convertToMono (input) {
        const splitter = this.audioContext.createChannelSplitter(2)
        const merger = this.audioContext.createChannelMerger(2)

        input.connect(splitter)
        splitter.connect(merger, 0, 0)
        splitter.connect(merger, 0, 1)
        return merger
      },
      updateAnalysers () {
        if (!this.analyserContext) {
          const { can } = this.$refs
          this.canvasWidth = can.width
          this.canvasHeight = can.height
          this.analyserContext = can.getContext('2d')
        }

        // analyzer draw code here
        let SPACING = 8
        let BAR_WIDTH = 1
        let numBars = Math.round(this.canvasWidth / SPACING)
        let freqByteData = new Uint8Array(this.analyserNode.frequencyBinCount)

        this.analyserNode.getByteFrequencyData(freqByteData)

        this.analyserContext.clearRect(0, 0, this.canvasWidth, this.canvasHeight)
        this.analyserContext.fillStyle = '#F6D565'
        this.analyserContext.lineCap = 'round'
        let multiplier = this.analyserNode.frequencyBinCount / numBars

        // Draw rectangle for each frequency bin
        for (let i = 0; i < numBars; ++i) {
          let magnitude = 0
          let offset = Math.floor(i * multiplier)

          // gotta sum/average the block, or we miss narrow-bandwidth spikes
          for (let j = 0; j < multiplier; j++) {
            magnitude += freqByteData[offset + j]
          }
          magnitude = magnitude / multiplier

          // let magnitude2 = freqByteData[i * multiplier]
          this.analyserContext.fillStyle = 'gray'
          this.analyserContext.fillRect(i * SPACING, (this.canvasHeight / 2) - (-magnitude / 2), BAR_WIDTH, -magnitude)
        }

        this.rafID = window.requestAnimationFrame(this.updateAnalysers)
      },
      gotStream (stream) {
        this.inputPoint = this.audioContext.createGain()

        // Create an AudioNode from the stream.
        this.realAudioInput = this.audioContext.createMediaStreamSource(stream)
        this.audioInput = this.realAudioInput
        this.audioInput.connect(this.inputPoint)

        // audioInput = convertToMono( input );

        this.analyserNode = this.audioContext.createAnalyser()
        this.analyserNode.fftSize = 2048
        this.inputPoint.connect(this.analyserNode)

        this.audioRecorder = new Recorder(this.inputPoint, { numChannels })

        let zeroGain = this.audioContext.createGain()
        zeroGain.gain.value = 0.0
        this.inputPoint.connect(zeroGain)
        zeroGain.connect(this.audioContext.destination)
        this.updateAnalysers()
      },
      initAudio() {
        if (!navigator || !navigator.mediaDevices) {
          this.audioUnavailable = true
          return
        }

        this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
        navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
          this.audioReady = true
          this.streamReference = stream
          // wait for dom
          this.$nextTick(() => {
            this.gotStream(stream)
          })
        }).catch((err) => {
          console.log(err)
          this.audioUnavailable = true
        })
      },
      makeMP3 () {
        if (!this.buffers) return
        let worker = new MP3Worker()
        worker.onmessage = (event) => {
          const { name, payload } = event.data
          if (name === 'progress') {
            this.progress = payload
            return
          }
          if (name === 'complete') {
            let name = this.recording.download.replace('.wav', '.mp3')
            payload.name = name
            this.file = payload
            this.mp3 = {
              href: createObjectURL(payload),
              download: name
            }
            this.recording = null
            this.$nextTick(() => {
              this.converting = false
            })
            return
          }
          // ignore other events
          // console.log('worker event', name, payload)
        }
        this.converting = true
        // start the worker
        worker.postMessage({ name: 'start', payload: this.buffers, numChannels })
      },
      startOver () {
        this.recording = null
        this.mp3 = null
      }
    },
    beforeDestroy () {
      if (!this.audioReady) return
      this.streamReference.getTracks().forEach((track) => {
        track.stop()
      })
      this.audioContext.close()
    },
    components: { Microphone, FieldNoteForm }
  }
</script>

<style>
  .progress-bar { height: 4px; }
</style>
